import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  ModalController,
  Platform,
  RefresherCustomEvent,
  ToastController
} from '@ionic/angular';
import { PdffakturaPage } from './pdffaktura/pdffaktura.page';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { DataService } from '../../services/datatransfer/DataService';
import { FakturaService } from '../../services/faktura/faktura.service';
import {
  Faktura,
  FakturaResponse,
  Fakturaanlegg
} from '../../services/faktura/faktura.types';
import { Avtaler } from '../../services/avtale/avtale.types';
import { AvtaleService } from '../../services/avtale/avtale.service';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { formatDateToString } from 'src/app/utils/date-utils';
import {
  dagerTilForfall,
  dagerTilForfallTekst,
  erRegningBetalt,
  getFakturaPeriodString,
  getValgtFakturering,
  harRegningForfalt
} from './regninger-utils';
import { takeUntil } from 'rxjs/operators';
import { InformationService } from 'src/app/services/notify/information.service';
import { InformationMessage } from 'src/app/services/notify/information.types';

@Component({
  selector: 'app-regninger',
  templateUrl: 'regninger.page.html',
  styleUrls: ['regninger.page.scss']
})
export class RegningerPage implements OnInit {
  fakturaList: Faktura[];
  modalPage: HTMLIonModalElement;
  adrDropDown: any;
  selected_adr: string;
  showRegningModal: number;

  subscriptionInit = false;

  anleggList: Observable<Fakturaanlegg[]>;
  valgtAnlegg: Fakturaanlegg;
  _showAnleggsliste: boolean;
  _showMPID = false;

  _fakturaResponse: Observable<FakturaResponse>;
  hasFetchedRegninger = false;

  valgtAvtale: Avtaler = null;
  avtaler: Avtaler[] = [];
  hasFetchedAvtaler = false;

  showPlaceholderRegning = false;
  placeholderFrom: string;
  placeholderTo: string;

  formatDateToString = formatDateToString;
  getFakturaPeriodString = getFakturaPeriodString;
  getValgtFakturering = getValgtFakturering;
  harRegningForfalt = harRegningForfalt;
  erRegningBetalt = erRegningBetalt;
  dagerTilForfall = dagerTilForfall;
  dagerTilForfallTekst = dagerTilForfallTekst;

  avtaleSubscription: Subscription;
  fakturaSubscription: Subscription;
  dataSubscription: Subscription;
  pdfDestroy$ = new Subject<void>();
  kunde_id: string;
  infoMessages: InformationMessage[] = [];

  constructor(
    private http: HttpClient,
    private dataService: DataService,
    private fakturaService: FakturaService,
    public platform: Platform,
    private _avtaleService: AvtaleService,
    public _toast: ToastController,
    private analyticsService: AnalyticsService,
    public modalCtrl: ModalController,
    public infoService: InformationService
  ) {}

  ngOnInit() {
    this.fatchData();
  }

  fatchData() {
    this.infoService.getInfoMessages().subscribe({
      next: (res) => {
        this.infoMessages = res.filter((x) => x.aktivert) || [];
      }
    });
    if (!this._fakturaResponse) {
      this._fakturaResponse = this.fakturaService.getFakturaList();
    }

    this.avtaleSubscription = this._avtaleService.getAvtaler().subscribe({
      next: (avtaleres) => {
        this.avtaler = avtaleres.avtaler;
        this.setAvtale();
        this.hasFetchedAvtaler = true;
        this.kunde_id = avtaleres.avtaler[0]?.kunde_id;
      }
    });

    this.fetchRegninger();
  }

  ionViewWillEnter() {
    this.dataSubscription = this.dataService.getKundebytte().subscribe((x) => {
      if (x !== this.kunde_id) {
        // Faktura: vi må reloade pga kundebytte
        this.hasFetchedRegninger = false;
        this.valgtAvtale = null;
        this.fakturaList = [];
        this.hasFetchedAvtaler = false;
        this.fatchData();
      }
    });
  }

  ionViewWillLeave() {
    this.avtaleSubscription.unsubscribe();
    this.fakturaSubscription.unsubscribe();
    this.dataSubscription.unsubscribe();
    this.pdfDestroy$.next(undefined);
    this.pdfDestroy$.complete();
  }

  async doRefresh(myevent: RefresherCustomEvent) {
    this.hasFetchedRegninger = false;
    this.valgtAvtale = null;
    this.fakturaList = [];

    this.hasFetchedAvtaler = false;
    this.avtaleSubscription = this._avtaleService.getAvtaler().subscribe({
      next: (avtaleres) => {
        this.avtaler = avtaleres.avtaler;
        this.setAvtale();
        this.hasFetchedAvtaler = true;
      }
    });
    this.fetchRegninger();

    myevent.detail.complete();
  }

  fetchRegninger() {
    this.showRegningModal = undefined;
    this.fakturaSubscription = this._fakturaResponse.subscribe({
      next: (res) => {
        if (res.list) {
          const alleanlegg: Fakturaanlegg[] = [];

          res.list.sort((a, b) =>
            new Date(a.forfdato) < new Date(b.forfdato)
              ? 1
              : new Date(b.forfdato) < new Date(a.forfdato)
              ? -1
              : 0
          );
          // const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
          const months = [
            'Januar',
            'Februar',
            'Mars',
            'April',
            'Mai',
            'Juni',
            'Juli',
            'August',
            'September',
            'Oktober',
            'November',
            'Desember'
          ];
          const yrs = [];
          res.list.forEach((element) => {
            if (
              element.maalepunktid !== null &&
              element.maalepunktid !== undefined
            ) {
              element.mnd = months[new Date(element.fakturadato).getMonth()];

              const year = new Date(element.fakturadato).getFullYear();
              if (!yrs.includes(year)) {
                element.yr = year;
                yrs.push(year);
              }
              element.belopNo = element.belop.toLocaleString('nb-no');

              const adrAdded =
                alleanlegg.filter(function (item) {
                  return item.maalepunktid == element.maalepunktid;
                }).length > 0
                  ? true
                  : false;

              if (!adrAdded) {
                const nyttAnlegg: Fakturaanlegg = {
                  maalepunktid: element.maalepunktid,
                  adresse: element.adresse,
                  postnr: element.postnr,
                  poststed: element.poststed,
                  mpid: element.maalepunktid.slice(
                    element.maalepunktid.length - 8
                  ),
                  beskrivelse: element.beskrivelse
                };

                const adressExist =
                  alleanlegg.filter(function (item) {
                    return item.adresse == element.adresse;
                  }).length > 0
                    ? true
                    : false;
                if (adressExist) this._showMPID = false; //Denne kan vi skru på om vi ønsker å vise mpid i anlegg-dropdown

                alleanlegg.push(nyttAnlegg);
              }
            }
          });

          // alleanlegg.sort((a, b) => (a.maalepunktid > b.maalepunktid ? 1 : -1));

          this.anleggList = of(alleanlegg);
          if (alleanlegg.length > 0) {
            this.valgtAnlegg = alleanlegg[0];
          }
          if (alleanlegg.length > 1) {
            this._showAnleggsliste = true;
          } else {
            this._showAnleggsliste = false;
          }

          this.setAvtale();

          this.fakturaList = res.list;

          // Vis placeholder regning?
          const today = new Date();
          if (today.getDate() > 6 && this.fakturaList.length > 0) {
            const thisFakturaDato = new Date(
              today.getFullYear(),
              today.getMonth(),
              1
            );
            const sisteFaktura = this.fakturaList.reduce((acc, curr) =>
              new Date(acc.tildato) > new Date(curr.tildato) ? acc : curr
            );
            const sisteFakturaDato = new Date(sisteFaktura.fakturadato);
            if (sisteFakturaDato < thisFakturaDato) {
              // Venter på regning
              this.showPlaceholderRegning = true;
              this.placeholderFrom = sisteFaktura.tildato;
              this.placeholderTo = thisFakturaDato.toDateString();
            }
          } else {
            this.showPlaceholderRegning = false;
          }

          this.hasFetchedRegninger = true;
        }
      },
      error: (err) => {
        this.hasFetchedRegninger = true;
      }
    });
  }

  setAnlegg(anlegg: Fakturaanlegg) {
    this.valgtAnlegg = anlegg;
    this.setAvtale();
  }

  public setAvtale() {
    this.valgtAvtale = null;
    if (
      this.valgtAnlegg &&
      this.valgtAnlegg != undefined &&
      this.valgtAnlegg.maalepunktid &&
      this.avtaler &&
      this.avtaler.length > 0
    ) {
      const mpid = this.valgtAnlegg.maalepunktid;
      const x = this.avtaler.filter(function (item) {
        return item.objectid == mpid;
      });
      if (x.length > 0) this.valgtAvtale = x[0];
    }
  }

  public async openRegningModal(fakturaNr: number) {
    this.showRegningModal = fakturaNr;
    this.analyticsService.logEvent_selectContent('regninger åpne detaljer');
  }

  async openPdf({
    faktura,
    showNettleieId
  }: {
    faktura: Faktura;
    showNettleieId: string;
  }) {
    if (faktura) {
      let url =
        'https://apiprivat.ishavskraft.no/faktura/pdf/view/' +
        faktura.fakturanr.toString();

      if (showNettleieId) {
        url =
          'https://apiprivat.ishavskraft.no/faktura/gjennomfakt/pdf/view/' +
          showNettleieId;
      }

      const pdfModal = await this.modalCtrl.create({
        component: PdffakturaPage,
        cssClass: 'show-faktura-modal-content'
      });
      const response = this.http.get(url, {
        responseType: 'blob',
        headers: { Accept: 'application/pdf' }
      });
      return response.pipe(takeUntil(this.pdfDestroy$)).subscribe({
        next: (res) => {
          pdfModal.componentProps = {
            pdfData: res,
            invoNo: faktura.fakturanr.toString()
          };
          return pdfModal.present();
        },
        error: async (err) => {
          //
        }
      });
    }
  }

  public closeModal() {
    if (this.modalPage)
      this.modalPage.dismiss({
        dismissed: true
      });
  }

  createAlert(message: any) {
    this._toast
      .create({
        message: message,
        duration: 4000,
        position: 'middle'
      })
      .then((toast) => {
        toast.present();
      });
  }

  anleggIsInactive(anlegg: Fakturaanlegg, avtaler: Avtaler[]) {
    return !avtaler?.some((x) => x.objectid === anlegg.maalepunktid);
  }
}
